import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { numberToString } from '../../components/special/numberConverter';

const StyledBar = styled(LinearProgress)(({ barcolor, height = 14 }) => ({
  height,
  borderRadius: height / 2,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'rgba(128,128,128,0.2)',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 4,
    backgroundColor: barcolor,
  },
}));

const BarRow = ({ label, value, color, noData, markerAt, markerColor }) => {
  const displayValue = noData ? '–' : numberToString(value, 1) + ' %';
  const barValue = noData ? 0 : Math.min(value / 2, 100);

  return (
    <Box className="flex items-center gap-1">
      <Typography
        variant="caption"
        sx={{ width: 120, flexShrink: 0, whiteSpace: 'nowrap', opacity: 0.6 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, position: 'relative', minWidth: 100 }}>
        <StyledBar
          variant="determinate"
          value={barValue}
          barcolor={noData ? 'rgba(128,128,128,0.3)' : color}
          sx={{ height: 'clamp(6px, 0.8vw, 10px)', borderRadius: 'clamp(3px, 0.4vw, 5px)' }}
        />
        {markerAt != null && !noData && markerAt > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: `${Math.min(markerAt / 2, 100)}%`,
              width: 2,
              height: '100%',
              backgroundColor: markerColor,
              borderRadius: 1,
            }}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        className="monospace"
        sx={{
          width: 55,
          flexShrink: 0,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          color: !noData && value > 100 ? color : undefined,
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

const ProjectControllingBars = ({ row }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const workProgress = row.workPackageProgress || 0;
  const billingProgress =
    row.orderedSumNetto > 0
      ? (row.invoicedSumNetto / row.orderedSumNetto) * 100
      : 0;
  const costProgress =
    row.totalTimePlannedSeconds > 0
      ? (row.totalTimeWorkedSeconds / row.totalTimePlannedSeconds) * 100
      : 0;
  return (
    <Box className="flex flex-col gap-1 py-1 md:ml-auto" sx={{ minWidth: 200, maxWidth: { md: 400 } }}>
      <BarRow
        label={t('Work Progress')}
        value={workProgress}
        color={colors.success}
      />
      <BarRow
        label={t('Billing Status')}
        value={billingProgress}
        color={colors.info}
        noData={row.orderedSumNetto === 0}
        markerAt={workProgress}
        markerColor={colors.success}
      />
      <BarRow
        label={t('Cost Status')}
        value={costProgress}
        color={costProgress > 100 ? colors.error : colors.warning}
        noData={row.totalTimePlannedSeconds === 0}
      />
    </Box>
  );
};

export default ProjectControllingBars;
